import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, rm } from "../../lib/api";
import { useAuth, useLang } from "../../lib/ctx";
import { MY_STATES } from "../../lib/i18n";
import { OrderView } from "./Orders";
import { ProductGrid } from "../../components/store/ProductCard";

const TABS = ["orders", "wishlist", "addresses", "receipts", "warranties", "tickets", "profile"];

const useGet = (path, dep) => {
  const [d, setD] = useState(null);
  const load = () => api.get(path).then((r) => setD(r.data)).catch(() => setD([]));
  useEffect(() => {
    load();
  }, [path, dep]); // eslint-disable-line react-hooks/exhaustive-deps
  return [d, load];
};

const OrdersTab = ({ receipts }) => {
  const [o] = useGet("/account/orders");
  if (!o) return <p className="text-zinc-500">Loading…</p>;
  const list = receipts ? o.filter((x) => x.invoice_no) : o;
  if (!list.length) return <p className="text-zinc-500" data-testid="account-empty">Nothing here yet.</p>;
  if (receipts) return <div className="space-y-2">{list.map((x) => <Link key={x.id} to={`/invoice/${x.number}`} className="card-dark flex justify-between p-4 text-sm" data-testid={`receipt-${x.number}`}><span>{x.invoice_no} · {x.receipt_no}</span><span>{rm(x.total)}</span></Link>)}</div>;
  return list.map((x) => <OrderView key={x.id} o={x} />);
};

const WishlistTab = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (user.wishlist?.length) api.get(`/products?ids=${user.wishlist.join(",")}`).then((r) => setItems(r.data));
  }, [user.wishlist]);
  return items.length ? <ProductGrid items={items} /> : <p className="text-zinc-500">No saved items.</p>;
};

const AddressesTab = () => {
  const [list, load] = useGet("/account/addresses");
  const [f, setF] = useState({ label: "Home", recipient: "", phone: "", line1: "", line2: "", city: "", state: "Selangor", postcode: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/account/addresses", f); load(); toast.success("Saved"); } catch (x) { toast.error(errMsg(x)); }
  };
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="space-y-3">{(list || []).map((a) => (
        <div key={a.id} className="card-dark p-4 text-sm" data-testid="saved-address"><p className="eyebrow">{a.label}</p><p className="mt-2">{a.recipient} {a.phone}</p><p className="text-zinc-400">{a.line1} {a.line2}, {a.postcode} {a.city}, {a.state}</p>
          <button onClick={() => api.delete(`/account/addresses/${a.id}`).then(load)} className="mt-2 text-xs text-red-400">Delete</button></div>))}
      </div>
      <form onSubmit={add} className="space-y-2" data-testid="address-form">
        {["label", "recipient", "phone", "line1", "line2", "city"].map((k) => <input key={k} placeholder={k} value={f[k]} onChange={s(k)} required={["line1", "city"].includes(k)} className="field" data-testid={`address-${k}-input`} />)}
        <select value={f.state} onChange={s("state")} className="field">{MY_STATES.map((x) => <option key={x}>{x}</option>)}</select>
        <input placeholder="postcode" pattern="\d{5}" required value={f.postcode} onChange={s("postcode")} className="field" data-testid="address-postcode-input" />
        <button className="btn-gold" data-testid="address-save-btn">Save address</button>
      </form>
    </div>
  );
};

const WarrantiesTab = () => {
  const [w] = useGet("/account/warranties");
  return !w?.length ? <p className="text-zinc-500">No warranty records.</p> : w.map((x) => <div key={x.id} className="card-dark mb-2 p-4 text-sm"><p className="font-semibold">{x.title}</p><p className="text-zinc-400">{x.months} months from {fmtDate(x.start)} · {x.order_number} · {x.terms}</p></div>);
};

const TicketsTab = () => {
  const [list, load] = useGet("/account/tickets");
  const [f, setF] = useState({ subject: "", message: "", order_number: "" });
  const [reply, setReply] = useState({});
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/account/tickets", f); setF({ subject: "", message: "", order_number: "" }); load(); toast.success("Ticket created"); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="space-y-3">{(list || []).map((tk) => (
        <div key={tk.id} className="card-dark p-4 text-sm" data-testid={`ticket-${tk.number}`}><p className="font-mono text-xs text-[#C8A45D]">{tk.number} · {tk.status}</p><p className="font-semibold">{tk.subject}</p>
          {tk.messages.map((m, i) => <p key={i} className={`mt-2 ${m.from === "staff" ? "text-[#C8A45D]" : "text-zinc-300"}`}>{m.from}: {m.text}</p>)}
          <div className="mt-2 flex gap-2"><input value={reply[tk.id] || ""} onChange={(e) => setReply({ ...reply, [tk.id]: e.target.value })} className="field" placeholder="Reply" />
            <button onClick={() => api.post(`/account/tickets/${tk.id}/reply`, { text: reply[tk.id] }).then(() => { setReply({}); load(); })} className="btn-ghost px-3">Send</button></div>
        </div>))}
      </div>
      <form onSubmit={create} className="space-y-2" data-testid="ticket-form">
        <input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="field" data-testid="ticket-subject-input" />
        <input placeholder="Order number (optional)" value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} className="field" />
        <textarea required rows={5} placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="field" data-testid="ticket-message-input" />
        <button className="btn-gold" data-testid="ticket-submit-btn">Open ticket</button>
      </form>
    </div>
  );
};

const ProfileTab = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", marketing_consent: !!user.marketing_consent });
  const save = async (e) => {
    e.preventDefault();
    try { setUser((await api.put("/account/profile", f)).data); toast.success("Saved"); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <form onSubmit={save} className="max-w-md space-y-3" data-testid="profile-form">
      <p className="text-sm text-zinc-400">{user.email} · Referral code <b className="text-[#C8A45D]" data-testid="referral-code">{user.referral_code}</b></p>
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field" data-testid="profile-name-input" />
      <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="field" placeholder="Phone" />
      <label className="flex gap-2 text-xs text-zinc-400"><input type="checkbox" checked={f.marketing_consent} onChange={(e) => setF({ ...f, marketing_consent: e.target.checked })} className="accent-[#C8A45D]" />Marketing emails</label>
      <button className="btn-gold" data-testid="profile-save-btn">Save</button>
    </form>
  );
};

export default function Account() {
  const { tab = "orders" } = useParams();
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  if (loading || user === null) return <p className="p-20 text-center text-zinc-500">Loading…</p>;
  if (!user) return <Navigate to="/login" replace />;
  const C = { orders: <OrdersTab />, receipts: <OrdersTab receipts />, wishlist: <WishlistTab />, addresses: <AddressesTab />, warranties: <WarrantiesTab />, tickets: <TicketsTab />, profile: <ProfileTab /> }[tab];
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-12" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">{t("account")}</p><h1 className="mt-2 font-display text-5xl sm:text-6xl">Hi, {user.name.split(" ")[0]}</h1></div>
        <div className="flex gap-2">{["admin", "staff"].includes(user.role) && <Link to="/admin" className="btn-gold" data-testid="go-admin-btn">Admin</Link>}<button onClick={() => logout().then(() => nav("/"))} className="btn-ghost" data-testid="logout-btn">{t("logout")}</button></div>
      </div>
      <div className="no-scrollbar mt-8 flex gap-6 overflow-x-auto border-b border-white/10">
        {TABS.map((k) => <Link key={k} to={`/account/${k}`} className={`whitespace-nowrap pb-3 text-xs font-semibold uppercase tracking-widest ${tab === k ? "border-b-2 border-[#C8A45D] text-[#C8A45D]" : "text-zinc-400"}`} data-testid={`account-tab-${k}`}>{t(k)}</Link>)}
      </div>
      <div className="mt-8">{C}</div>
    </div>
  );
}
